import { Component, type ErrorInfo, type ReactNode } from "react";
import { recordDiagnostic } from "@/lib/diagnostics";

/**
 * Contains a render crash to the part of the screen that threw (web #8).
 *
 * Without one, a single malformed transcript row or a sidebar bug replaced the
 * whole organization — sidebar included — with the router's default error,
 * and the data that caused it was restored from this browser's cache on the
 * next load, so a reload threw again. Each boundary reports to the
 * diagnostics ring, and its fallback offers a way back: Try again re-renders
 * (with `onReset` first, which can drop the cached data that crashed).
 */
export class ErrorBoundary extends Component<
  {
    /** Where this boundary sits, for diagnostics. */
    name: string;
    children: ReactNode;
    fallback?: (props: { error: Error; reset: () => void }) => ReactNode;
    /** Runs before a reset, e.g. to forget the cached data that crashed. */
    onReset?: () => void;
    /** A change here clears the error (another chat, another task). */
    resetKey?: unknown;
  },
  { error: Error | null; resetKey: unknown }
> {
  state: { error: Error | null; resetKey: unknown } = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: unknown) {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  static getDerivedStateFromProps(props: { resetKey?: unknown }, state: { error: Error | null; resetKey: unknown }) {
    if (props.resetKey !== state.resetKey) return { error: null, resetKey: props.resetKey };
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[${this.props.name}] render failed`, error, info.componentStack);
    recordDiagnostic(`render:${this.props.name}`, `${error.name}: ${error.message}${info.componentStack ? `\n${info.componentStack.split("\n").slice(0, 6).join("\n")}` : ""}`);
  }

  private reset = () => {
    this.props.onReset?.();
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback({ error, reset: this.reset });
    return (
      <div className="error-boundary" role="alert">
        <div className="error-state-title">This part of Rivets could not be shown.</div>
        <div className="error-state-hint">{error.message}</div>
        <button type="button" className="btn btn-sm error-state-action" onClick={this.reset}>Try again</button>
      </div>
    );
  }
}

/** One transcript entry that could not be drawn: the rest of the chat stays. */
export function RowErrorFallback({ error, raw }: { error: Error; raw?: unknown }) {
  return (
    <div className="chat-row-error" role="note">
      <span>This entry could not be displayed.</span>
      {raw !== undefined && (
        <button
          type="button"
          className="chat-send-action"
          title={error.message}
          onClick={() => { void navigator.clipboard?.writeText(safeJson(raw)); }}
        >Copy JSON</button>
      )}
    </div>
  );
}

function safeJson(value: unknown): string {
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
}

/** The page-level fallback (router `defaultErrorComponent`): a reload, or a
 * reload with this browser's cache cleared, which is the way out when the
 * cached data itself is what throws. */
export function RouteErrorFallback({ error, onClearCache }: { error: unknown; onClearCache: () => Promise<void> }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <main className="page route-error" role="alert">
      <div className="error-state-title">Something went wrong showing this page.</div>
      <div className="error-state-hint">{message}</div>
      <div className="route-error-actions">
        <button type="button" className="btn btn-sm" onClick={() => window.location.reload()}>Reload</button>
        <button type="button" className="btn btn-sm" onClick={() => { void onClearCache().finally(() => window.location.reload()); }}>Clear cache and reload</button>
      </div>
    </main>
  );
}
